import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import SmallModalLayout from '../../../../../shared/components/structure/SmallModalLayout';
import ModalHeader from '../../../../../shared/components/general/ModalHeader';
import FooterBar from '../../../../../shared/components/structure/FooterBar';
import Button from '../../../../../shared/components/general/Button';
import CustomScrollbars from '../../../../../shared/components/structure/CustomScrollbars';
import {
    color_text_light,
    color_shades_darkest,
    color_variants_decline_light_1,
    color_colors_decline,
    color_variants_shadow_20,
    highlight_green,
    green_1
} from '../../../../../shared/constants/colors';
import { FieldGenerateResult } from './autoGenerateResultUtils';

const alertIcon = require('../../../../../shared/content/images/alert-circle.svg');
const successIcon = require('../../../../../shared/content/images/green-check-circle-2.svg');

export interface AutoGenerateResultsModalProps {
    showModal: boolean;
    onConfirm: () => void;
    successFields: FieldGenerateResult[];
    failedFields: FieldGenerateResult[];
    hasApiError: boolean;
    apiErrors: string[];
    isLoading: boolean;
}

const MAX_VALUE_DISPLAY_LENGTH = 80;

function truncateValue(value: string): string {
    if (!value || value.length <= MAX_VALUE_DISPLAY_LENGTH) return value;
    return value.slice(0, MAX_VALUE_DISPLAY_LENGTH) + '…';
}

/** Groups an array of FieldGenerateResult by appName, preserving insertion order of groups. */
function groupByAppName(fields: FieldGenerateResult[]): Array<{ appName: string | undefined; fields: FieldGenerateResult[] }> {
    const order: Array<string | undefined> = [];
    const map = new Map<string | undefined, FieldGenerateResult[]>();
    for (const field of fields) {
        const key = field.appName;
        if (!map.has(key)) {
            order.push(key);
            map.set(key, []);
        }
        map.get(key).push(field);
    }
    return order.map(k => ({ appName: k, fields: map.get(k) }));
}

const AutoGenerateResultsModal: React.FC<AutoGenerateResultsModalProps> = (props) => {

    const hasSuccesses = props.successFields.length > 0;
    const hasFailures = props.failedFields.length > 0;
    const totalProcessed = props.successFields.length + props.failedFields.length;

    // Determine whether any field carries an appName so we know whether to render group headers
    const hasAppGroups = props.successFields.some(f => f.appName) || props.failedFields.some(f => f.appName);

    const renderSummaryText = () => {
        if (props.hasApiError) return null;
        if (totalProcessed === 0) {
            return (
                <SummaryText>
                    No fields were eligible for auto-generation.
                </SummaryText>
            );
        }
        return (
            <SummaryText>
                {props.successFields.length} of {totalProcessed} field{totalProcessed !== 1 ? 's' : ''} successfully generated.
            </SummaryText>
        );
    };

    const renderFieldRows = (fields: FieldGenerateResult[], isSuccess: boolean, keyPrefix: string) =>
        fields.map((field, index) => (
            <FieldRow key={keyPrefix + index}>
                <FieldTitle>{field.fieldTitle}</FieldTitle>
                {isSuccess
                    ? <FieldValue>&ldquo;{truncateValue(field.generatedValue)}&rdquo;</FieldValue>
                    : (
                        <FailedValueRow>
                            <FieldValueFailed>Unknown</FieldValueFailed>
                            {field.retryAttempted && (
                                <RetryBadge>Both URLs tried</RetryBadge>
                            )}
                        </FailedValueRow>
                    )
                }
            </FieldRow>
        ));

    const renderSuccessFields = () => {
        if (!hasSuccesses) return null;
        const groups = groupByAppName(props.successFields);
        return (
            <ResultSection>
                <SectionHeader isSuccess>
                    <SectionIcon src={successIcon} />
                    Successfully Updated ({props.successFields.length})
                </SectionHeader>
                {hasAppGroups
                    ? groups.map((group, gi) => (
                        <AppGroup key={'sg-' + gi}>
                            {group.appName && <AppGroupLabel>{group.appName}</AppGroupLabel>}
                            {renderFieldRows(group.fields, true, `sg-${gi}-`)}
                        </AppGroup>
                    ))
                    : renderFieldRows(props.successFields, true, 'success-')
                }
            </ResultSection>
        );
    };

    const renderFailedFields = () => {
        if (!hasFailures) return null;
        const groups = groupByAppName(props.failedFields);
        return (
            <ResultSection hasBorderTop={hasSuccesses}>
                <SectionHeader isSuccess={false}>
                    <SectionIcon src={alertIcon} />
                    Needs Manual Update ({props.failedFields.length})
                </SectionHeader>
                <FailedNote>
                    A value could not be found for these fields. You will need to fill them in manually.
                </FailedNote>
                {hasAppGroups
                    ? groups.map((group, gi) => (
                        <AppGroup key={'fg-' + gi}>
                            {group.appName && <AppGroupLabel>{group.appName}</AppGroupLabel>}
                            {renderFieldRows(group.fields, false, `fg-${gi}-`)}
                        </AppGroup>
                    ))
                    : renderFieldRows(props.failedFields, false, 'failed-')
                }
            </ResultSection>
        );
    };

    return (
        <SmallModalLayout className={modalStyle} isVisible={props.showModal}>
            <ModalHeader title="Autogenerate Results" />
            {props.hasApiError && !props.isLoading && (
                <ErrorBanner>
                    <AlertIcon src={alertIcon} />
                    <ErrorMessages>
                        {props.apiErrors.map((err, i) => (
                            <span key={i}>{err}</span>
                        ))}
                    </ErrorMessages>
                </ErrorBanner>
            )}
            <ModalBody>
                {renderSummaryText()}
                <ScrollableResults>
                    <CustomScrollbars
                        autoHide
                        style={{ height: '100%' }}
                    >
                        <div>
                            {renderSuccessFields()}
                            {renderFailedFields()}
                            {!props.hasApiError && totalProcessed === 0 && (
                                <EmptyState>
                                    No fields were processed. Make sure you provided a valid URL or file with relevant content.
                                </EmptyState>
                            )}
                        </div>
                    </CustomScrollbars>
                </ScrollableResults>
            </ModalBody>
            <FooterBar>
                <Button
                    type="button"
                    themes={['primary']}
                    text="OK"
                    loading={props.isLoading}
                    disabled={props.isLoading}
                    onClick={props.onConfirm}
                />
            </FooterBar>
        </SmallModalLayout>
    );
};

export default AutoGenerateResultsModal;

const modalStyle = css`
    height: 80%;
    width: 30%;
    min-width: 450px;
`;

const ModalBody = styled.div`
    padding: 32px;
    height: calc(100% - 160px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const SummaryText = styled.p`
    font-size: 14px;
    margin-bottom: 16px;
    color: ${color_shades_darkest};
`;

const ScrollableResults = styled.div`
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const ResultSection = styled.div<{ hasBorderTop?: boolean }>`
    padding-top: ${props => props.hasBorderTop ? '24px' : '0'};
    margin-top: ${props => props.hasBorderTop ? '24px' : '0'};
    border-top: ${props => props.hasBorderTop ? `1px solid ${color_variants_shadow_20}` : 'none'};
`;

const SectionHeader = styled.div<{ isSuccess: boolean }>`
    display: flex;
    align-items: center;
    font-weight: bold;
    font-size: 14px;
    margin-bottom: 12px;
    color: ${props => props.isSuccess ? green_1 : color_colors_decline};
`;

const SectionIcon = styled.img`
    width: 20px;
    height: 20px;
    margin-right: 8px;
`;

const FailedNote = styled.p`
    font-size: 13px;
    color: ${color_text_light};
    margin-bottom: 12px;
`;

const FieldRow = styled.div`
    padding: 8px 0;
    border-bottom: 1px solid ${color_variants_shadow_20};
    &:last-of-type {
        border-bottom: none;
    }
`;

const FieldTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${color_shades_darkest};
    margin-bottom: 2px;
`;

const FieldValue = styled.div`
    font-size: 12px;
    font-style: italic;
    color: ${color_text_light};
    background: ${highlight_green};
    border-radius: 4px;
    padding: 2px 6px;
    margin-top: 2px;
    display: inline-block;
`;

const FieldValueFailed = styled.div`
    font-size: 12px;
    font-style: italic;
    color: ${color_colors_decline};
    background: ${color_variants_decline_light_1};
    border-radius: 4px;
    padding: 2px 6px;
    margin-top: 2px;
    display: inline-block;
`;

const FailedValueRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 2px;
`;

const RetryBadge = styled.div`
    font-size: 11px;
    color: ${color_text_light};
    border: 1px solid ${color_variants_shadow_20};
    border-radius: 4px;
    padding: 1px 5px;
    display: inline-block;
`;

const EmptyState = styled.p`
    font-size: 13px;
    color: ${color_text_light};
    font-style: italic;
`;

const AppGroup = styled.div`
    margin-bottom: 8px;
`;

const AppGroupLabel = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: ${color_text_light};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 10px 0 4px;
    padding-bottom: 4px;
    border-bottom: 1px solid ${color_variants_shadow_20};
`;

const ErrorBanner = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    font-family: Muli, "sans-serif";
    font-weight: normal;
    color: ${color_shades_darkest};
    background-color: ${color_variants_decline_light_1};
    border: 1px solid ${color_colors_decline};
    border-radius: 8px;
    padding: 8px 16px;
    margin: 16px 32px 0 32px;
    font-size: 14px;
`;

const AlertIcon = styled.img`
    margin-right: 8px;
    margin-top: 2px;
    flex-shrink: 0;
`;

const ErrorMessages = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;
